/**
 * CRM notification settings (hub admin)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import HubLayout from '../components/hub/HubLayout'
import CrmHelpLink from '../components/hub/crm/CrmHelpLink'
import { isHubCrmAdmin } from '../components/hub/crm/CrmSettingsLink'
import { useSchedulerAuth } from '../contexts/SchedulerAuthContext'
import { hubCrmAPI } from '../utils/hub/api'
import { GROUP_LABELS } from '../constants/hub-groups'
import {
  IDLE_THRESHOLD_FIELDS,
  CLIENT_FOLLOWUP_THRESHOLD_FIELDS,
  CHANNEL_LABELS,
} from '../lib/crm-settings-display'

function GroupMembersTooltip({ members }) {
  const list = members || []
  const count = list.length
  if (!count) return null

  const withEmail = list.filter((m) => m.receives_crm_email).length
  const withPhone = list.filter((m) => m.receives_crm_phone).length
  let summary = count === 1 ? '1 member' : `${count} members`
  if (withEmail === 0) summary += ' · none with email'
  else if (withEmail < count) summary += ` · ${withEmail} with email`
  if (withPhone > 0 && withPhone < count) summary += ` · ${withPhone} with phone`
  else if (withPhone === 0) summary += ' · none with phone'

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="mt-1 cursor-help text-xs text-gray-600 underline decoration-dotted underline-offset-2 hover:text-gray-900"
            aria-label={`View ${count} group member${count === 1 ? '' : 's'}`}
          >
            {summary}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs whitespace-normal py-2 text-left">
          <span className="mb-1 block font-medium text-gray-200">Members</span>
          <ul className="space-y-0.5">
            {list.map((m) => (
              <li key={m.id}>
                {m.name}
                {!m.receives_crm_email ? (
                  <span className="text-gray-400"> (no email on file)</span>
                ) : null}
                {!m.receives_crm_phone ? (
                  <span className="text-gray-400"> (no phone on file)</span>
                ) : null}
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        className="mt-1 shrink-0 rounded border-gray-300 text-green-600 focus:ring-green-600"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="text-sm font-medium text-gray-900">{label}</span>
        {description ? (
          <span className="block text-sm text-gray-500 mt-0.5">{description}</span>
        ) : null}
      </span>
    </label>
  )
}

export default function HubCRMSettings() {
  const { user } = useSchedulerAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [values, setValues] = useState({})
  const [groups, setGroups] = useState([])
  const [channelOptions, setChannelOptions] = useState([])
  const [clientFollowup, setClientFollowup] = useState({
    channels: ['email'],
    channelOptions: [],
    twilio_configured: false,
  })
  const [schedules, setSchedules] = useState([])
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewSubject, setPreviewSubject] = useState('')
  const [previewMeta, setPreviewMeta] = useState(null)
  const [testLeadId, setTestLeadId] = useState('')
  const [testLoading, setTestLoading] = useState('')
  const [followupPreviewHtml, setFollowupPreviewHtml] = useState('')
  const [followupPreviewSms, setFollowupPreviewSms] = useState('')
  const [followupPreviewSpeech, setFollowupPreviewSpeech] = useState('')
  const [followupPreviewSubject, setFollowupPreviewSubject] = useState('')
  const [followupPreviewMeta, setFollowupPreviewMeta] = useState(null)
  const [followupTestVariant, setFollowupTestVariant] = useState('contacted')
  const [followupTestLang, setFollowupTestLang] = useState('en')
  const [followupTestPhone, setFollowupTestPhone] = useState('')
  const [followupTestEmail, setFollowupTestEmail] = useState('')
  const [followupTestFeedback, setFollowupTestFeedback] = useState(null)
  const [callTestCooldownSec, setCallTestCooldownSec] = useState(0)
  const followupTestRef = useRef(null)
  const callCooldownTimerRef = useRef(null)

  const TEST_CALL_COOLDOWN_SEC = 60

  useEffect(() => {
    if (callTestCooldownSec <= 0) {
      if (callCooldownTimerRef.current) {
        clearInterval(callCooldownTimerRef.current)
        callCooldownTimerRef.current = null
      }
      return undefined
    }
    callCooldownTimerRef.current = setInterval(() => {
      setCallTestCooldownSec((s) => {
        if (s <= 1) {
          clearInterval(callCooldownTimerRef.current)
          callCooldownTimerRef.current = null
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => {
      if (callCooldownTimerRef.current) {
        clearInterval(callCooldownTimerRef.current)
        callCooldownTimerRef.current = null
      }
    }
  }, [callTestCooldownSec > 0])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await hubCrmAPI.getSettings()
      const s = data.settings
      setValues(s.values || {})
      setGroups(s.groups || [])
      setChannelOptions(s.channelOptions || [])
      setClientFollowup(
        s.clientFollowup || {
          channels: ['email'],
          channelOptions: [],
          twilio_configured: false,
        }
      )
      setSchedules(s.schedules || [])
    } catch (err) {
      setError(err.message || 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (user && isHubCrmAdmin(user)) load()
  }, [user, load])

  useEffect(() => {
    if (user) {
      setFollowupTestPhone(user.phone || '')
      setFollowupTestEmail(user.email || '')
    }
  }, [user?.id, user?.phone, user?.email])

  if (!user) return null
  if (!isHubCrmAdmin(user)) {
    return <Navigate to="/hub/crm" replace />
  }

  const setValue = (key, val) => {
    setValues((v) => ({ ...v, [key]: val }))
  }

  const setClientChannel = (channelId, enabled) => {
    setClientFollowup((prev) => {
      const channels = new Set(prev.channels || [])
      if (enabled) channels.add(channelId)
      else channels.delete(channelId)
      return { ...prev, channels: [...channels] }
    })
  }

  const setGroupChannel = (groupName, channelId, enabled) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.name !== groupName) return g
        const channels = new Set(g.channels || [])
        if (enabled) channels.add(channelId)
        else channels.delete(channelId)
        return { ...g, channels: [...channels] }
      })
    )
  }

  const buildPatchBody = () => {
    const channelMap = {}
    for (const g of groups) {
      channelMap[g.name] = g.channels || []
    }
    return {
      ...values,
      crm_idle_notify_enabled: values.crm_idle_notify_enabled === '1' ? '1' : '0',
      crm_new_lead_notify: values.crm_new_lead_notify === '1' ? '1' : '0',
      crm_client_followup_enabled:
        values.crm_client_followup_enabled === '1' ? '1' : '0',
      crm_group_notify_channels: channelMap,
      crm_client_followup_channels: clientFollowup.channels?.length
        ? clientFollowup.channels
        : ['email'],
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const data = await hubCrmAPI.updateSettings(buildPatchBody())
      const s = data.settings
      setValues(s.values || {})
      setGroups(s.groups || [])
      setClientFollowup(
        s.clientFollowup || {
          channels: ['email'],
          channelOptions: [],
          twilio_configured: false,
        }
      )
      setMessage('Settings saved.')
    } catch (err) {
      setError(err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const parseTestLeadId = () => {
    const trimmed = testLeadId.trim()
    if (!trimmed) return undefined
    const n = parseInt(trimmed, 10)
    return Number.isNaN(n) || n < 1 ? undefined : n
  }

  const showFollowupTestFeedback = (feedback) => {
    setFollowupTestFeedback(feedback)
    followupTestRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  const runTestClientFollowup = async (channel, send) => {
    if (send && channel === 'ligacao') {
      if (!followupTestPhone.trim()) {
        const text =
          'Enter a phone number in Test phone above (or save one in HUB Profile).'
        setError(text)
        showFollowupTestFeedback({ ok: false, text })
        return
      }
      if (!clientFollowup.voice_configured) {
        const text =
          'Twilio Voice is not configured. Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_SMS_FROM to Worker secrets, then refresh this page.'
        setError(text)
        showFollowupTestFeedback({ ok: false, text })
        return
      }
    }

    setTestLoading(send ? `followup-${channel}-send` : `followup-${channel}-preview`)
    setError('')
    setMessage('')
    setFollowupTestFeedback(null)
    setFollowupPreviewMeta(null)
    try {
      const lead_id = parseTestLeadId()
      const data = await hubCrmAPI.testClientFollowup({
        variant: followupTestVariant,
        channel,
        language: followupTestLang,
        send,
        lead_id,
        test_phone: followupTestPhone,
        test_email: followupTestEmail,
      })
      if (channel === 'sms') {
        setFollowupPreviewSms(data.preview_sms || '')
        setFollowupPreviewHtml('')
        setFollowupPreviewSubject('')
        setFollowupPreviewSpeech('')
      } else if (channel === 'ligacao') {
        setFollowupPreviewSpeech(data.preview_speech || '')
        setFollowupPreviewHtml('')
        setFollowupPreviewSubject('')
        setFollowupPreviewSms('')
      } else {
        setFollowupPreviewHtml(data.preview_html || '')
        setFollowupPreviewSubject(data.subject || '')
        setFollowupPreviewSms('')
        setFollowupPreviewSpeech('')
      }
      if (data.intake_url || data.base_url) {
        setFollowupPreviewMeta({
          lead_id: data.lead_id,
          intake_url: data.intake_url,
          base_url: data.base_url,
          preview_mock: data.preview_mock,
          preview_fallback: data.preview_fallback,
        })
      }
      if (send) {
        const successText =
          channel === 'sms'
            ? `Test SMS sent to ${data.to || 'your profile phone'}.`
            : channel === 'ligacao'
              ? data.call_warning
                ? data.call_warning
                : data.call_status === 'completed'
                  ? `Twilio reports the call connected (${data.call_duration_sec ?? '?'}s) to ${data.call_to || data.to || 'your profile phone'}. If you did not answer live, check voicemail — the test script may be there.${
                      data.call_sid ? ` SID: ${data.call_sid}` : ''
                    }`
                  : `Test call placed to ${data.call_to || data.to || 'your profile phone'}. Answer to hear the script.${
                      data.call_sid ? ` (Twilio SID: ${data.call_sid})` : ''
                    }`
              : `Test email sent to ${data.to || followupTestEmail.trim() || user.email}.`
        setMessage(successText)
        if (channel === 'ligacao') {
          setCallTestCooldownSec(TEST_CALL_COOLDOWN_SEC)
        }
        showFollowupTestFeedback({
          ok: true,
          text: successText,
          warn: Boolean(channel === 'ligacao' && data.call_warning),
        })
      } else if (channel === 'ligacao' && data.preview_speech) {
        showFollowupTestFeedback({ ok: true, text: 'Call script preview loaded below.' })
      } else if (channel === 'sms' && data.preview_sms) {
        showFollowupTestFeedback({ ok: true, text: 'SMS preview loaded below.' })
      }
    } catch (err) {
      const detail = err.data?.twilio_code
        ? `${err.message} (Twilio ${err.data.twilio_code})`
        : err.message || 'Test failed'
      setError(detail)
      showFollowupTestFeedback({ ok: false, text: detail })
      if (send && channel === 'ligacao') {
        setCallTestCooldownSec(TEST_CALL_COOLDOWN_SEC)
      }
    } finally {
      setTestLoading('')
    }
  }

  const runTestEmail = async (template, send) => {
    setTestLoading(send ? `${template}-send` : `${template}-preview`)
    setError('')
    setPreviewMeta(null)
    try {
      const lead_id = parseTestLeadId()
      const data = await hubCrmAPI.testNotificationEmail({ template, send, lead_id })
      setPreviewHtml(data.preview_html || '')
      setPreviewSubject(data.subject || '')
      if (data.lead_url || data.base_url) {
        setPreviewMeta({
          lead_id: data.lead_id,
          lead_url: data.lead_url,
          base_url: data.base_url,
          preview_mock: data.preview_mock,
          preview_fallback: data.preview_fallback,
        })
      }
      if (send) {
        setMessage(
          data.sent
            ? `Test email sent to ${user.email}.`
            : `Send failed: ${(data.errors || []).join(', ') || 'unknown error'}`
        )
      }
    } catch (err) {
      setError(err.message || 'Test failed')
    } finally {
      setTestLoading('')
    }
  }

  return (
    <HubLayout appName="CRM Settings">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/hub/crm"
            className="text-sm text-green-700 hover:underline mb-1 inline-block"
          >
            ← Back to CRM
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900">CRM settings</h1>
          <p className="text-sm text-gray-600 mt-1">
            Notification thresholds, team channels, and scheduled jobs.
          </p>
        </div>
        <CrmHelpLink />
      </div>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}
      {message ? (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {message}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <form onSubmit={handleSave} className="space-y-8">
          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Team notifications</h2>
            <div className="space-y-4">
              <Toggle
                label="Email on new case"
                description="Send immediately when a case is created (forms, HUB, intake)."
                checked={values.crm_new_lead_notify === '1'}
                onChange={(on) => setValue('crm_new_lead_notify', on ? '1' : '0')}
              />
              <Toggle
                label="Email on idle cases"
                description="Daily job alerts when cases exceed idle thresholds below."
                checked={values.crm_idle_notify_enabled === '1'}
                onChange={(on) => setValue('crm_idle_notify_enabled', on ? '1' : '0')}
              />
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-1">SMS &amp; phone (Twilio)</h2>
            <p className="text-sm text-gray-500 mb-4">
              Control SMS and phone separately. Turn SMS off while A2P registration is under review;
              keep phone on when Twilio Voice is ready. Email is not affected.
            </p>
            <div className="space-y-4">
              <Toggle
                label="Enable phone (click-to-call &amp; voice alerts)"
                description="When off, CRM hides call actions and skips automated phone alerts to your team."
                checked={values.crm_phone_enabled === '1'}
                onChange={(on) => setValue('crm_phone_enabled', on ? '1' : '0')}
              />
              <Toggle
                label="Enable SMS (text messages)"
                description="When off, CRM hides SMS actions and skips automated text follow-ups until A2P is approved."
                checked={values.crm_sms_enabled === '1'}
                onChange={(on) => setValue('crm_sms_enabled', on ? '1' : '0')}
              />
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-1">Idle thresholds (days)</h2>
            <p className="text-sm text-gray-500 mb-4">
              Days without activity before a status counts as idle for team alerts.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {IDLE_THRESHOLD_FIELDS.map(({ key, label }) => (
                <label key={key} className="block">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <input
                    type="number"
                    min={0}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={values[key] ?? ''}
                    onChange={(e) => setValue(key, e.target.value)}
                  />
                </label>
              ))}
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Repeat alert interval</span>
                <input
                  type="number"
                  min={0}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={values.idle_repeat_interval_days ?? ''}
                  onChange={(e) => setValue('idle_repeat_interval_days', e.target.value)}
                />
                <span className="text-xs text-gray-500 mt-1 block">
                  Days between repeat idle emails for the same case.
                </span>
              </label>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-1">Notification channels by group</h2>
            <p className="text-sm text-gray-500 mb-4">
              CRM case alerts only — not related to Room Scheduler “Notification preference” on
              user profiles. Each row is a group with <strong>CRM case notifications</strong>. When{' '}
              <strong>Email</strong> is checked, active users in that group (with an email address)
              receive new-case and idle alerts. <strong>SMS / phone call</strong> require a phone
              number on each user in Hub → Users. Manage membership in <strong>Hub → Users</strong>.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    <th className="py-2 pr-4 font-medium">Group</th>
                    {(channelOptions.length
                      ? channelOptions
                      : Object.keys(CHANNEL_LABELS).map((id) => ({ id, enabled: id === 'email' }))
                    ).map((ch) => (
                      <th key={ch.id} className="py-2 px-3 font-medium text-center">
                        {CHANNEL_LABELS[ch.id] || ch.label || ch.id}
                        {!ch.enabled ? (
                          <span className="block text-xs font-normal text-gray-400">
                            {ch.id === 'ligacao' ? 'set Twilio SMS' : 'soon'}
                          </span>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.id} className="border-b border-gray-100">
                      <td className="py-3 pr-4 align-top">
                        <div className="font-medium text-gray-900">
                          {GROUP_LABELS[g.name] || g.name}
                        </div>
                        {g.members?.length ? (
                          <GroupMembersTooltip members={g.members} />
                        ) : (
                          <p className="mt-1 text-xs text-amber-700">
                            No users in this group — add members in Hub → Users.
                          </p>
                        )}
                        {g.channels?.includes('email') && g.email_recipient_count === 0 ? (
                          <p className="mt-1 text-xs text-amber-700">
                            Email is on but no one here can receive mail yet.
                          </p>
                        ) : null}
                        {(g.channels?.includes('whatsapp') || g.channels?.includes('ligacao')) &&
                        g.phone_recipient_count === 0 ? (
                          <p className="mt-1 text-xs text-amber-700">
                            SMS or phone call is on but no one here has a phone number yet.
                          </p>
                        ) : null}
                      </td>
                      {(channelOptions.length
                        ? channelOptions
                        : [{ id: 'email', enabled: true }]
                      ).map((ch) => (
                        <td key={ch.id} className="py-3 px-3 text-center">
                          <input
                            type="checkbox"
                            disabled={!ch.enabled}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-600 disabled:opacity-40"
                            checked={(g.channels || []).includes(ch.id)}
                            onChange={(e) =>
                              setGroupChannel(g.name, ch.id, e.target.checked)
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-1">Client follow-up (automated)</h2>
            <p className="text-sm text-gray-500 mb-4">
              Reminders to prospective clients who opted in on the website forms (checkbox on by
              default) or were created in CRM. Couples receive both partners; minors go to the
              guardian.
            </p>
            <div className="space-y-4 mb-6">
              <Toggle
                label="Enable automated client follow-up"
                description="Daily job sends intake reminders for Contacted / Intake sent cases that exceed the thresholds below (max attempts per case)."
                checked={values.crm_client_followup_enabled === '1'}
                onChange={(on) => setValue('crm_client_followup_enabled', on ? '1' : '0')}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {CLIENT_FOLLOWUP_THRESHOLD_FIELDS.map(({ key, label }) => (
                <label key={key} className="block">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <input
                    type="number"
                    min={0}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={values[key] ?? ''}
                    onChange={(e) => setValue(key, e.target.value)}
                  />
                </label>
              ))}
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Max attempts per case</span>
                <input
                  type="number"
                  min={1}
                  max={10}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={values.crm_client_followup_max_attempts ?? ''}
                  onChange={(e) => setValue('crm_client_followup_max_attempts', e.target.value)}
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Repeat interval (days)</span>
                <input
                  type="number"
                  min={0}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={values.client_followup_repeat_interval_days ?? ''}
                  onChange={(e) =>
                    setValue('client_followup_repeat_interval_days', e.target.value)
                  }
                />
              </label>
            </div>
            <p className="text-sm font-medium text-gray-700 mb-2">Channels</p>
            <div className="flex flex-wrap gap-6">
              {(clientFollowup.channelOptions?.length
                ? clientFollowup.channelOptions
                : [{ id: 'email', enabled: true, label: 'Email' }]
              ).map((ch) => (
                <label
                  key={ch.id}
                  className={`flex items-center gap-2 text-sm ${!ch.enabled ? 'opacity-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-green-600 focus:ring-green-600"
                    disabled={!ch.enabled}
                    checked={(clientFollowup.channels || []).includes(ch.id)}
                    onChange={(e) => setClientChannel(ch.id, e.target.checked)}
                  />
                  {ch.label || CHANNEL_LABELS[ch.id] || ch.id}
                  {!ch.enabled && ch.id === 'sms' ? (
                    <span className="text-xs text-gray-400">(set Twilio SMS secrets)</span>
                  ) : null}
                  {!ch.enabled && ch.id === 'whatsapp' ? (
                    <span className="text-xs text-gray-400">(later — Business WhatsApp)</span>
                  ) : null}
                </label>
              ))}
            </div>
            {clientFollowup.sms_configured ||
            clientFollowup.voice_configured ||
            clientFollowup.twilio_configured ? (
              <p className="text-xs text-green-700 mt-3">
                Twilio is configured
                {clientFollowup.sms_configured ? ' (SMS follow-up enabled)' : ''}
                {clientFollowup.voice_configured && values.crm_phone_enabled === '1'
                  ? ' · voice alerts and click-to-call available'
                  : ''}
                . Voice uses the same number as TWILIO_SMS_FROM.
              </p>
            ) : values.crm_phone_enabled === '0' && values.crm_sms_enabled === '0' ? (
              <p className="text-xs text-amber-700 mt-3">
                SMS and phone are turned off in CRM settings above.
              </p>
            ) : values.crm_sms_enabled === '0' ? (
              <p className="text-xs text-amber-700 mt-3">
                SMS is off in CRM settings (e.g. while A2P is pending). Phone can stay on when Voice
                is ready.
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-3">
                SMS and voice need TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and TWILIO_SMS_FROM in Worker
                secrets. WhatsApp can be enabled later.
              </p>
            )}

            <div ref={followupTestRef} className="mt-8 pt-6 border-t border-gray-100">
              <h3 className="text-sm font-medium text-gray-900 mb-1">Test client follow-up & phone</h3>
              <p className="text-sm text-gray-500 mb-2">
                Preview or send a test email or SMS to your HUB profile (same as the daily client
                follow-up job). <strong>Phone call</strong> rings your profile number with a short
                test script (works when Voice is enabled in Twilio, even if SMS/A2P is still
                pending). Production idle-lead calls use a separate script in the codebase.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Test email, SMS, and calls use the addresses below (defaults from your{' '}
                <Link to="/hub/profile" className="text-green-700 underline">HUB Profile</Link>
                ). Wait at least {TEST_CALL_COOLDOWN_SEC} seconds between test calls; rapid retries often
                show as <em>busy</em> in Twilio even when your phone is fine.
              </p>
              <div className="flex flex-wrap gap-4 mb-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Variant</span>
                  <select
                    className="mt-1 block rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={followupTestVariant}
                    onChange={(e) => setFollowupTestVariant(e.target.value)}
                  >
                    <option value="contacted">Contacted</option>
                    <option value="intake_sent">Intake sent</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Language</span>
                  <select
                    className="mt-1 block rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={followupTestLang}
                    onChange={(e) => setFollowupTestLang(e.target.value)}
                  >
                    <option value="en">English</option>
                    <option value="pt">Portuguese</option>
                  </select>
                </label>
                <label className="block min-w-[12rem] flex-1">
                  <span className="text-sm font-medium text-gray-700">Test phone (SMS &amp; call)</span>
                  <input
                    type="tel"
                    autoComplete="tel"
                    className="mt-1 block w-full min-w-[10rem] rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={followupTestPhone}
                    onChange={(e) => setFollowupTestPhone(e.target.value)}
                    placeholder="(555) 555-0100"
                  />
                </label>
                <label className="block min-w-[12rem] flex-1">
                  <span className="text-sm font-medium text-gray-700">Test email</span>
                  <input
                    type="email"
                    autoComplete="email"
                    className="mt-1 block w-full min-w-[10rem] rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={followupTestEmail}
                    onChange={(e) => setFollowupTestEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </label>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                <Button
                  type="button"
                  variant="secondary" size="sm"
                  disabled={!!testLoading}
                  onClick={() => runTestClientFollowup('email', false)}
                >
                  {testLoading === 'followup-email-preview' ? 'Loading…' : 'Preview email'}
                </Button>
                <Button
                  type="button"
                  variant="secondary" size="sm"
                  disabled={!!testLoading || !followupTestEmail.trim()}
                  onClick={() => runTestClientFollowup('email', true)}
                >
                  {testLoading === 'followup-email-send' ? 'Sending…' : 'Send email test'}
                </Button>
                <Button
                  type="button"
                  variant="secondary" size="sm"
                  disabled={!!testLoading}
                  onClick={() => runTestClientFollowup('sms', false)}
                >
                  {testLoading === 'followup-sms-preview' ? 'Loading…' : 'Preview SMS'}
                </Button>
                <Button
                  type="button"
                  variant="secondary" size="sm"
                  disabled={
                    !!testLoading ||
                    !clientFollowup.sms_twilio_configured ||
                    !followupTestPhone.trim() ||
                    values.crm_sms_enabled === '0'
                  }
                  onClick={() => runTestClientFollowup('sms', true)}
                >
                  {testLoading === 'followup-sms-send' ? 'Sending…' : 'Send SMS test'}
                </Button>
                <Button
                  type="button"
                  variant="secondary" size="sm"
                  disabled={!!testLoading}
                  onClick={() => runTestClientFollowup('ligacao', false)}
                >
                  {testLoading === 'followup-ligacao-preview' ? 'Loading…' : 'Preview call script'}
                </Button>
                <Button
                  type="button"
                  variant="secondary" size="sm"
                  disabled={!!testLoading || callTestCooldownSec > 0}
                  onClick={() => runTestClientFollowup('ligacao', true)}
                >
                  {testLoading === 'followup-ligacao-send'
                    ? 'Calling… (up to 60s)'
                    : callTestCooldownSec > 0
                      ? `Wait ${callTestCooldownSec}s…`
                      : 'Place test call'}
                </Button>
              </div>
              {values.crm_phone_enabled === '0' ? (
                <p className="text-xs text-gray-600 mb-3">
                  Production phone features are off above, but you can still place a test call here to
                  verify Twilio Voice.
                </p>
              ) : null}
              {followupTestFeedback ? (
                <div
                  className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
                    followupTestFeedback.ok
                      ? followupTestFeedback.warn
                        ? 'border-amber-200 bg-amber-50 text-amber-900'
                        : 'border-green-200 bg-green-50 text-green-800'
                      : 'border-red-200 bg-red-50 text-red-800'
                  }`}
                  role="status"
                >
                  {followupTestFeedback.text}
                </div>
              ) : null}
              {followupPreviewMeta?.preview_mock ? (
                <p className="text-sm text-amber-700 mb-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                  Using mock sample data. Link is for layout preview only.
                </p>
              ) : followupPreviewMeta?.preview_fallback ? (
                <p className="text-sm text-gray-600 mb-2">
                  Using your latest CRM case for preview.
                </p>
              ) : null}
              {followupPreviewSms ? (
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 whitespace-pre-wrap">
                  <p className="text-xs font-medium text-gray-500 mb-1">SMS preview</p>
                  {followupPreviewSms}
                </div>
              ) : null}
              {followupPreviewSpeech ? (
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
                  <p className="text-xs font-medium text-gray-500 mb-1">Phone call script (TTS)</p>
                  <p className="whitespace-pre-wrap">{followupPreviewSpeech}</p>
                </div>
              ) : null}
              {followupPreviewHtml ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {followupPreviewSubject ? (
                    <p className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-b border-gray-200">
                      Subject: {followupPreviewSubject}
                    </p>
                  ) : null}
                  <iframe
                    title="Client follow-up email preview"
                    srcDoc={followupPreviewHtml}
                    className="w-full min-h-[280px] bg-white"
                    sandbox=""
                  />
                </div>
              ) : null}
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-1">Scheduled jobs (Cloudflare Worker)</h2>
            <p className="text-sm text-gray-500 mb-4">
              These run automatically in production. Use manual triggers only for testing (requires
              cron secret in server config).
            </p>
            <ul className="space-y-4">
              {schedules.map((job) => (
                <li
                  key={job.id}
                  className="rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm"
                >
                  <p className="font-medium text-gray-900">{job.label}</p>
                  <p className="text-gray-600 mt-1">{job.description}</p>
                  <dl className="mt-2 grid gap-1 text-gray-700">
                    <div>
                      <dt className="inline font-medium">Schedule: </dt>
                      <dd className="inline">
                        {(job.cronDescriptions || job.crons || []).join(' · ')}
                      </dd>
                    </div>
                    <div>
                      <dt className="inline font-medium">Cron: </dt>
                      <dd className="inline font-mono text-xs">
                        {(job.crons || []).join(', ')}
                      </dd>
                    </div>
                    <div>
                      <dt className="inline font-medium">HTTP: </dt>
                      <dd className="inline font-mono text-xs">{job.httpEndpoint}</dd>
                    </div>
                    {job.workerJob ? (
                      <div>
                        <dt className="inline font-medium">Worker: </dt>
                        <dd className="inline font-mono text-xs">{job.workerJob}</dd>
                      </div>
                    ) : null}
                  </dl>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-1">Test email templates</h2>
            <p className="text-sm text-gray-500 mb-4">
              Preview the HTML below, or send a test to <strong>{user.email}</strong> (requires{' '}
              <code className="text-xs bg-gray-100 px-1 rounded">RESEND_API_KEY</code> in local
              Worker env). Links use this site&apos;s origin (local or production). For idle
              alerts, leave case ID empty to use the first eligible idle case, or enter a specific
              ID.
            </p>
            <label className="block max-w-xs mb-4">
              <span className="text-sm font-medium text-gray-700">Case ID (optional)</span>
              <input
                type="number"
                min={1}
                placeholder="e.g. 4"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={testLeadId}
                onChange={(e) => setTestLeadId(e.target.value)}
              />
            </label>
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                type="button"
                variant="secondary" size="sm"
                disabled={!!testLoading}
                onClick={() => runTestEmail('new_lead', false)}
              >
                {testLoading === 'new_lead-preview' ? 'Loading…' : 'Preview new case'}
              </Button>
              <Button
                type="button"
                variant="secondary" size="sm"
                disabled={!!testLoading}
                onClick={() => runTestEmail('new_lead', true)}
              >
                {testLoading === 'new_lead-send' ? 'Sending…' : 'Send new case test'}
              </Button>
              <Button
                type="button"
                variant="secondary" size="sm"
                disabled={!!testLoading}
                onClick={() => runTestEmail('idle', false)}
              >
                {testLoading === 'idle-preview' ? 'Loading…' : 'Preview idle alert'}
              </Button>
              <Button
                type="button"
                variant="secondary" size="sm"
                disabled={!!testLoading}
                onClick={() => runTestEmail('idle', true)}
              >
                {testLoading === 'idle-send' ? 'Sending…' : 'Send idle test'}
              </Button>
            </div>
            {previewMeta?.preview_mock ? (
              <p className="text-sm text-amber-700 mb-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                Using mock sample data (no idle cases in CRM). Link is for layout preview only.
              </p>
            ) : previewMeta?.preview_fallback ? (
              <p className="text-sm text-gray-600 mb-2">
                Using your latest CRM case (not currently idle) for preview.
              </p>
            ) : null}
            {previewMeta?.lead_url ? (
              <p className="text-sm text-gray-600 mb-3">
                Preview link:{' '}
                <a
                  href={previewMeta.lead_url}
                  className="text-green-700 font-medium break-all hover:underline"
                >
                  {previewMeta.lead_url}
                </a>
                {previewMeta.lead_id != null ? (
                  <span className="text-gray-500"> (case #{previewMeta.lead_id})</span>
                ) : null}
              </p>
            ) : null}
            {previewHtml ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {previewSubject ? (
                  <p className="text-xs text-gray-500 px-3 py-2 bg-gray-50 border-b border-gray-200">
                    Subject: {previewSubject}
                    {previewMeta?.base_url ? (
                      <span className="block mt-1">Base URL: {previewMeta.base_url}</span>
                    ) : null}
                  </p>
                ) : null}
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  className="w-full min-h-[320px] bg-white"
                  sandbox=""
                />
              </div>
            ) : null}
          </section>

          <div className="flex justify-end gap-3">
            <Button asChild variant="secondary">
              <Link to="/hub/crm">Cancel</Link>
            </Button>
            <Button type="submit" variant="success" disabled={saving}>
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        </form>
      )}
    </HubLayout>
  )
}
